// Левая колонка: список чатов.
//
// Живёт отдельно от сессии, потому что меняется от действий пользователя, а
// не от входа: закончился запуск — список другой. Обновляется по требованию,
// а не опросом.
//
// Проектов здесь больше нет (06.09.2026). Папка вокруг цели оказалась лишним
// уровнем: чат заводится на каждый вопрос и называется этим вопросом, а
// раскладывать их по папкам никто не стал. Сервер про проекты ещё знает — и
// память проектов вместе с ним, — но выбирать между «чатом» и «проектом»
// человек больше не должен.

import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ChatSummary } from '@smyslika/api-client'

import { useSession } from './session.tsx'

export interface WorkspaceValue {
  /** Чаты: закреплённые сверху, дальше свежие. Архив сюда не попадает. */
  chats: ChatSummary[]
  refresh: () => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null)

export function useWorkspace(): WorkspaceValue {
  const v = use(WorkspaceContext)
  if (v == null) throw new Error('useWorkspace вне WorkspaceProvider')
  return v
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { chats: api, signedIn, me } = useSession()
  const [chats, setChats] = useState<ChatSummary[]>([])

  const refresh = useCallback(async () => {
    if (signedIn !== true) return
    try {
      setChats((await api.list()).chats)
    } catch {
      // Боковая колонка — не то, ради чего стоит ронять экран. Прежний список
      // честнее страницы об ошибке поверх работы.
    }
  }, [api, signedIn])

  // Список — того, кто вошёл: сменился человек — прежний список снимается
  // сразу и читается заново. Без этого гость, которому молча завели нового
  // гостя (SMY-357), видел бы слева чаты прежнего — и открывал бы их в
  // «Этого чата здесь нет». `signedIn` при такой смене не меняется вовсе.
  const who = me?.userId ?? null
  useEffect(() => {
    setChats([])
  }, [who])

  useEffect(() => {
    void refresh()
  }, [refresh, who])

  const value = useMemo<WorkspaceValue>(() => ({ chats, refresh }), [chats, refresh])
  return <WorkspaceContext value={value}>{children}</WorkspaceContext>
}
